//--
import * as _ from 'lodash'
import * as AGChannel from 'ag-channel'

import {
    ILock,
    ILockObject,
    ILockOptions, ILockOwner,
    ILockServiceClient
} from "../PA.Concurrency.Core/PA.Concurrency.IF.Lock";
import {
    ILockServiceInternal, IServerLock,
    IServerLockAcquireReleaseResponse, IServerLockChannelMessage
} from "../PA.Concurrency.Core/PA.Concurrency.IF.ServerLock";
import {PAWebSocketClient} from "../PA.Core/PAWebSocketClient";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {IUsrContext} from "../PA.Core/PA.Core.IF.User";
import {Lock} from "../PA.Concurrency.Core/Lock";
import {ISessionContext} from "../PA.Core/PA.Core.IF.Session";


// Data Structure for client side locks

export class PALockClient
    implements
        ILockServiceClient
{

    private _paLockClientInternal: PALockClientInternal = undefined
    private static _statusOnlyLocks: {[ClassId: string]: {[objectId: string]: Lock}} = {}

    constructor() {
        // No local state
        this._paLockClientInternal = new PALockClientInternal()

    }



    createLock(classId: string, lockId: number | string, options?: ILockOptions): ILock {
        const self = this

        let bReadonly = !!(options?.bStatusOnly)
        let bAddToCache = false

        //Check if it's in the cache
        if(bReadonly){
            if(!(classId in PALockClient._statusOnlyLocks))
                PALockClient._statusOnlyLocks[classId] = {}

            if(lockId in PALockClient._statusOnlyLocks[classId]){
                return PALockClient._statusOnlyLocks[classId][lockId]
            } else {
                bAddToCache = true
            }

        }

        let lock = new Lock(classId, lockId, self._paLockClientInternal, options)

        if(bAddToCache)
            PALockClient._statusOnlyLocks[classId][lockId] = lock

        return lock

    }


    createLocks(classId: string, arrlockIds: (number | string)[], options?: ILockOptions): ILock[] {
        const self = this
        let returnValue: ILock[] = []
        _.forEach(arrlockIds, (lockId) => {
            returnValue.push(self.createLock(classId, lockId, options))
        })

        return returnValue
    }



    // Gets Readonly Locks
    getLock(classId: string, lockId: number | string, options?: ILockOptions): ILock {
        const self = this
        let opts: ILockOptions = {}

        _.defaults(opts, options)
        opts.bStatusOnly = true

        let aLock =self.createLock(classId, lockId, opts)
        aLock.acquire()
        return aLock
    }

    getLocks(classId: string, arrlockIds: (number | string)[], options?: ILockOptions): ILock[] {
        const self = this
        let returnValue: ILock[] = []
        _.forEach(arrlockIds, (lockId) => {
            returnValue.push(self.getLock(classId, lockId, options))
        })

        return returnValue
    }



}

export enum ILockInvokeRequestType {
    createServerLock= "createServerLock",
    createServerLocks = "createServerLocks",
    acquireServerLock = "acquireServerLock",
    releaseServerLock = "releaseServerLock",
    destroyServerLock = "destroyServerLock",
    breakServerLock = "breakServerLock",
    touchServerLock = "touchServerLock"
}

export interface ILockInvokeRequest {
    messageType: ILockInvokeRequestType,
    classId?: string,
    lockId?: number | string | (number | string)[],
    lockKey?: string,

    bStatusOnly?: boolean,

    object?: ILockObject,
    owner?: ILockOwner
}

export interface ILockInvokeResponse {
    bSuccess: boolean,
    sErrors: string[],
    results: string[]
}


class PALockClientInternal implements ILockServiceInternal {

    private  wsc: PAWebSocketClient;

    constructor() {
        //--
        this.wsc = new PAWebSocketClient()
    }

    async getLockMessageChannel(classId: string, lockId: number | string): Promise<AGChannel<IServerLockChannelMessage>> {
        const self = this
        // Don't change this convention, used in PALockService.sendClassObjectNodeUpdate
        let aChannelId = `Class_${classId}_Lock_${lockId}`
        let returnValue: AGChannel<IServerLockChannelMessage> = await self.wsc.getChannel(aChannelId)

        return returnValue
    }

    async createServerLock(classId: string, lockId: number | string, options?: ILockOptions): Promise<string> {
        const self = this

        let results: string = ""
        let opts: ILockOptions = {}
        _.defaults(opts, options)
        let usrCtx: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        let sessCtx: ISessionContext = PAEnvironment.getValue(PAEnvironment.SessionContext)

        let requestMessage: ILockInvokeRequest = {
            messageType: ILockInvokeRequestType.createServerLock,
            classId: classId,
            lockId: lockId,
            bStatusOnly: !!opts.bStatusOnly,
            object: opts.object,
            owner: {
                OwnerName: usrCtx?.User?.cif?.AUTHUSR_NAME,
                SessionId: sessCtx?.Session_GUID,
                HostName: undefined
            }
        }

        try {
            let opts: any = {}
            let response:ILockInvokeResponse  =  await self.wsc.invoke('LockService', requestMessage, opts)
            if(response.bSuccess)
                results = response.results[0]
        } catch(err) {
            console.log(`createServerLockException: ${err}`)
            results = ""
        }

        return results
    }

    async createServerLocks(classId: string, arrLockId: number | string, options?: ILockOptions): Promise<string[]> {
        const self = this

        let results: string[] = []
        let opts: ILockOptions = {}
        _.defaults(opts, options)
        let usrCtx: IUsrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        let sessCtx: ISessionContext = PAEnvironment.getValue(PAEnvironment.SessionContext)

        let requestMessage: ILockInvokeRequest = {
            messageType: ILockInvokeRequestType.createServerLocks,
            classId: classId,
            lockId: arrLockId,
            bStatusOnly: !!opts.bStatusOnly,
            object: opts.object,
            owner: {
                OwnerName: usrCtx?.User?.cif?.AUTHUSR_NAME,
                SessionId: sessCtx?.Session_GUID,
                HostName: undefined
            }
        }

        try {
            let opts: any = {}
            let response:ILockInvokeResponse  =  await self.wsc.invoke('LockService', requestMessage, opts)
            if(response.bSuccess)
                results.push(...response.results)
        } catch(err) {
            console.log(`createServerLocksException: ${err}`)
            results = []
        }

        return results
    }

    async acquireServerLock(lockKey: string, iTimeout?: number): Promise<IServerLockAcquireReleaseResponse> {
        const self = this

        let results: IServerLockAcquireReleaseResponse = undefined

        let requestMessage: ILockInvokeRequest = {
            messageType:  ILockInvokeRequestType.acquireServerLock,
            lockKey: lockKey
        }

        try {
            let opts: any = {}
            if (_.isNumber(iTimeout)) {
                opts.ackTimeout = iTimeout
            }
            let response:ILockInvokeResponse  =  await self.wsc.invoke('LockService', requestMessage, opts)
            results = {
                bSuccess: response.bSuccess,
                sFailureReason: response.sErrors[0],
                Lock: undefined,
            }
        } catch(err) {
            results = {
                bSuccess: false,
                sFailureReason: `acquireServerLockException: ${err}`,
                Lock: undefined
            }
        }

        return results
    }

    async releaseServerLock(lockKey: string, iTimeout?: number): Promise<IServerLockAcquireReleaseResponse> {
        const self = this

        let results: IServerLockAcquireReleaseResponse = undefined

        let requestMessage: ILockInvokeRequest = {
            messageType: ILockInvokeRequestType.releaseServerLock,
            lockKey: lockKey
        }

        try {
            let opts: any = {}
            if (_.isNumber(iTimeout)) {
                opts.ackTimeout = iTimeout
            }
            let response:ILockInvokeResponse  =  await self.wsc.invoke('LockService', requestMessage, opts)
            results = {
                bSuccess: response.bSuccess,
                sFailureReason: response.sErrors[0],
                Lock: undefined,
            }
        } catch(err) {
            results = {
                bSuccess: false,
                sFailureReason: `releaseServerLockException: ${err}`,
                Lock: undefined
            }
        }

        return results
    }

    async touchServerLock(lockKey: string, iTimeout?: number): Promise<void>{
        const self = this


        let requestMessage: ILockInvokeRequest = {
            messageType:  ILockInvokeRequestType.touchServerLock,
            lockKey: lockKey
        }

        try {
            let opts: any = {}
            if (_.isNumber(iTimeout)) {
                opts.ackTimeout = iTimeout
            }
            let response:ILockInvokeResponse  =  await self.wsc.invoke('LockService', requestMessage, opts)

        } catch(err) {
            console.log(`touchServerLockException: ${err}`)
        }

        return
    }


    async destroyServerLock(lockKey: string, iTimeout?: number): Promise<void>{
        const self = this


        let requestMessage: ILockInvokeRequest = {
            messageType:  ILockInvokeRequestType.destroyServerLock,
            lockKey: lockKey
        }

        try {
            let opts: any = {}
            if (_.isNumber(iTimeout)) {
                opts.ackTimeout = iTimeout
            }
            let response:ILockInvokeResponse  =  await self.wsc.invoke('LockService', requestMessage, opts)

        } catch(err) {
            console.log(`destroyServerLockException: ${err}`)
        }

        return
    }

}
